import { Button, Table, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { Eye, Package, Calendar, User } from "lucide-react";
import React, { useMemo, useState } from "react";
import { TBooking } from "~/types";
import { capitalize, formatDate, getClientBookingRelationship, getBookingDisplayInfo, calculateCubicFeetInventory } from "~/utils";
import { JobStatus } from "~/utils/constants";
import { useAuth } from "~/store";
import { BookingDetailSidebar } from "./booking-detail-sidebar";
import { RESPONSIVE_CLASSES, useResponsive } from "~/utils/responsive";

const { Text } = Typography;

interface BookingTableProps {
  bookings: TBooking[];
  loading?: boolean;
}

interface BookingRow {
  id: string;
  bookingId: string;
  serviceType: string;
  packages: number;
  date: string;
  project: string;
  status: JobStatus;
  rawBooking: TBooking;
  isServiceClient?: boolean;
  showLimitedBadge?: boolean;
  isSubclientBooking?: boolean;
  subclientName?: string | null;
}

// Mobile Card Component for responsive design
const BookingCard: React.FC<{ booking: BookingRow; onClick: () => void }> = ({ booking, onClick }) => (
  <div
    className="mb-3 cursor-pointer border border-[#e5e5e5] rounded-lg bg-white p-4 hover:bg-[#fafafa] transition-colors"
    onClick={onClick}
  >
    <div className="space-y-2">
      <div className={RESPONSIVE_CLASSES.flexJustifyBetween}>
        <div>
          <Text strong className="text-[#1a1a2e] font-bold">{booking.bookingId}</Text>
          {booking.showLimitedBadge && (
            <span className="ml-2 inline-block px-2 py-0.5 text-xs font-bold uppercase border border-[#e5e5e5] rounded-lg bg-gray-200 text-[#1a1a2e]">
              Service Client
            </span>
          )}
        </div>
        {getStatusBadge(booking.status)}
      </div>
      
      <div className="space-y-1">
        <div className={RESPONSIVE_CLASSES.flexItemsCenter}>
          <User size={14} className="text-[#1a1a2e]" />
          <Text className={RESPONSIVE_CLASSES.captionText}>{booking.serviceType}</Text>
        </div>
        
        <div className="flex items-center justify-between">
          <div className={RESPONSIVE_CLASSES.flexItemsCenter}>
            <Package size={14} className="text-[#1a1a2e]" />
            <Text className={RESPONSIVE_CLASSES.captionText}>{booking.packages} packages</Text>
          </div>
          
          <div className={RESPONSIVE_CLASSES.flexItemsCenter}>
            <Calendar size={14} className="text-[#1a1a2e]" />
            <Text className={RESPONSIVE_CLASSES.captionText}>{booking.date}</Text>
          </div>
        </div>
      </div>
      
      <div>
        <Text className={RESPONSIVE_CLASSES.captionText} ellipsis>
          {booking.project}
        </Text>
        {booking.isSubclientBooking && booking.subclientName && (
          <Text className="text-xs font-bold text-gray-600 block">
            --{booking.subclientName}--
          </Text>
        )}
      </div>
    </div>
  </div>
);

const getStatusBadge = (status: JobStatus) => {
  // In Progress is special - just italic black text, no box
  if (status === JobStatus.IN_PROGRESS) {
    return (
      <span className="text-[#1a1a2e] italic font-bold text-xs uppercase">
        In Progress
      </span>
    );
  }
  
  // Scheduled is special - just black text, no box, no italics
  if (status === JobStatus.SCHEDULED) {
    return (
      <span className="text-[#1a1a2e] font-bold text-xs uppercase">
        Scheduled
      </span>
    );
  }
  
  const statusConfig = {
    [JobStatus.DRAFT]: { text: "Draft", className: "bg-gray-200 text-[#1a1a2e]", extraClass: "" },
    [JobStatus.COMPLETED]: { text: "Completed", className: "bg-[#1a1a2e] text-white", extraClass: "" },
    [JobStatus.CANCELLED]: { text: "Cancelled", className: "bg-gray-200 text-[#1a1a2e]", extraClass: "" },
    [JobStatus.ESTIMATED]: { text: "Estimated", className: "bg-gray-200 text-[#1a1a2e]", extraClass: "" },
  };
  const config = statusConfig[status] || { text: status, className: "bg-gray-200 text-[#1a1a2e]", extraClass: "" };
  return (
    <span className={`inline-block px-2 py-1 text-xs font-bold uppercase border border-[#e5e5e5] rounded-lg ${config.className}`}>
      {config.text}
    </span>
  );
};

export const BookingTable: React.FC<BookingTableProps> = ({
  bookings,
  loading = false,
}) => {
  const [selectedBooking, setSelectedBooking] = useState<TBooking | null>(null);
  const { user } = useAuth();
  const currentClientId = user?.client?.id;
  const { isMobile, isTablet } = useResponsive();

  const tableData = useMemo(() => {
    if (!currentClientId) return [];

    return bookings.map((booking): BookingRow => {
      const relationshipData = getClientBookingRelationship(booking, currentClientId);
      const displayInfo = getBookingDisplayInfo(relationshipData);

      // For service clients, show only their relevant service tickets
      const relevantServiceTickets = relationshipData.relationship === 'service-client' 
        ? relationshipData.visibleServiceTickets 
        : booking.serviceTicket || [];

      // Count service types from relevant service tickets
      const serviceTypeCounts = relevantServiceTickets.reduce((acc, ticket) => {
        const type = ticket.serviceType;
        acc[type] = (acc[type] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);
      
      const serviceTypes = Object.entries(serviceTypeCounts)
        .map(([type, count]) => `${count} ${capitalize(type)}`)
        .join(", ") || "N/A";

      // Count unique packages/items across relevant service tickets (avoid duplicates)
      const inventoryMap = new Map<string, any>();
      relevantServiceTickets.forEach((ticket) => {
        ticket.inventories?.forEach((inventory) => {
          if (!inventoryMap.has(inventory.id)) {
            inventoryMap.set(inventory.id, inventory);
          }
        });
      });
      const totalPackages = inventoryMap.size;

      // Use booking date or first service date from relevant tickets
      const bookingDate =
        booking.bookingDate || relevantServiceTickets[0]?.serviceDatetime;

      // Build project/sidemark display
      const projectName = booking.project?.name;
      const sidemark = booking.sidemark;
      let projectDisplay = '-';
      if (projectName && sidemark) {
        projectDisplay = `${projectName} / ${sidemark}`;
      } else if (projectName) {
        projectDisplay = projectName;
      } else if (sidemark) {
        projectDisplay = sidemark;
      }

      // Check if booking belongs to a subclient of the logged-in user
      const isSubclientBooking = booking.client?.parentClientId === currentClientId;
      const subclientName = isSubclientBooking
        ? (booking.client?.companyName || `${booking.client?.firstName || ''} ${booking.client?.lastName || ''}`.trim())
        : null;

      return {
        id: booking.id,
        bookingId: displayInfo.title,
        serviceType: serviceTypes,
        packages: totalPackages,
        date: formatDate(bookingDate),
        project: projectDisplay,
        status: booking.status,
        rawBooking: booking,
        isServiceClient: relationshipData.relationship === 'service-client',
        showLimitedBadge: displayInfo.showLimitedBadge,
        isSubclientBooking,
        subclientName,
      };
    });
  }, [bookings, currentClientId]);

  const handleRowClick = (record: BookingRow) => {
    setSelectedBooking(record.rawBooking);
  };

  const handleSidebarClose = () => {
    setSelectedBooking(null);
  };

  const isSidebarOpen = selectedBooking !== null;

  const columns: ColumnsType<BookingRow> = [
    {
      title: "Booking ID",
      dataIndex: "bookingId",
      key: "bookingId",
      width: 140,
      render: (text: string, record: BookingRow) => (
        <div className="flex flex-col">
          <Text strong className="text-[#1a1a2e] font-bold">
            {text}
          </Text>
          {record.showLimitedBadge && (
            <span className="mt-1 inline-block px-2 py-0.5 text-xs font-bold uppercase border border-[#e5e5e5] rounded-lg bg-gray-200 text-[#1a1a2e] w-fit">
              Service Client
            </span>
          )}
        </div>
      ),
    },
    {
      title: "Project / Sidemark",
      dataIndex: "project",
      key: "project",
      width: 200,
      ellipsis: true,
      render: (text: string, record: BookingRow) => (
        <div className="flex flex-col">
          <span>{text}</span>
          {record.isSubclientBooking && record.subclientName && (
            <span className="text-xs font-bold text-gray-600">
              --{record.subclientName}--
            </span>
          )}
        </div>
      ),
    },
    {
      title: "Service Types",
      dataIndex: "serviceType",
      key: "serviceType",
      width: 200,
      render: (text: string) => <Text>{text}</Text>,
    },
    {
      title: "Container Info",
      dataIndex: "packages",
      key: "packages",
      width: 150,
      align: "center",
      render: (count: number, record: BookingRow) => {
        const relevantServiceTickets = record.isServiceClient 
          ? getClientBookingRelationship(record.rawBooking, currentClientId!)?.visibleServiceTickets || []
          : record.rawBooking.serviceTicket || [];
        
        // Get unique inventories across all service tickets (avoid duplicates)
        const inventoryMap = new Map<string, any>();
        relevantServiceTickets.forEach((ticket) => {
          ticket.inventories?.forEach((inventory) => {
            if (!inventoryMap.has(inventory.id)) {
              inventoryMap.set(inventory.id, inventory);
            }
          });
        });
        const uniqueInventories = Array.from(inventoryMap.values());
        
        // Calculate volume from unique inventories
        const stats = calculateCubicFeetInventory(uniqueInventories);
        const totalVolume = typeof stats.totalCubicFeet === 'number' 
          ? stats.totalCubicFeet 
          : parseFloat(stats.totalCubicFeet.toString()) || 0;

        return (
          <div className="text-center">
            <div className="flex items-center justify-center space-x-1">
              <Package size={14} className="text-[#1a1a2e]" />
              <Text strong className="text-[#1a1a2e]">{count}</Text>
            </div>
            <Text className="text-xs text-[#1a1a2e]">
              {totalVolume.toFixed(1)} ft³
            </Text>
          </div>
        );
      },
    },
    {
      title: "Created Date",
      dataIndex: "date",
      key: "date",
      width: 120,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 140,
      render: (status: JobStatus) => getStatusBadge(status),
    },
    {
      title: "Action",
      key: "action",
      width: 80,
      render: (_, record) => (
        <Button
          type="text"
          size="small"
          icon={<Eye size={16} />}
          onClick={(e) => {
            e.stopPropagation();
            handleRowClick(record);
          }}
        />
      ),
    },
  ];

  // Render mobile/tablet card layout
  if (isMobile || isTablet) {
    return (
      <div className="flex h-full relative">
        <div className={`flex-1 transition-all duration-300 ease-in-out ${isSidebarOpen ? "lg:mr-[600px]" : ""}`}>
          <div className={RESPONSIVE_CLASSES.container}>
            {loading ? (
              <div className="flex justify-center p-8">
                <div>Loading...</div>
              </div>
            ) : (
              <div>
                {tableData.map((booking) => (
                  <BookingCard
                    key={booking.id}
                    booking={booking}
                    onClick={() => handleRowClick(booking)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Booking Detail Sidebar */}
        <div
          className={`fixed right-0 top-0 h-full bg-white border-l border-[#e5e5e5] transition-all duration-300 ease-in-out z-50 ${
            isSidebarOpen ? "w-[600px] translate-x-0" : "w-[600px] translate-x-full"
          }`}
        >
          <BookingDetailSidebar
            booking={selectedBooking}
            onClose={handleSidebarClose}
          />
        </div>

        {/* Overlay for mobile */}
        {isSidebarOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-25 z-40 lg:hidden"
            onClick={handleSidebarClose}
          />
        )}
      </div>
    );
  }

  // Render desktop table layout
  return (
    <div className="flex h-full relative">
      <div className={`flex-1 min-w-0 max-w-full overflow-x-hidden transition-all duration-300 ease-in-out ${isSidebarOpen ? "mr-[600px]" : ""}`}>
        <div className="w-full max-w-full overflow-x-auto">
          <Table
            columns={columns}
            dataSource={tableData}
            loading={loading}
            rowKey="id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} bookings`,
            }}
            onRow={(record) => ({
              onClick: () => handleRowClick(record),
              className: "cursor-pointer hover:bg-[#fafafa] border-b border-[#e5e5e5]",
            })}
            scroll={{ x: 1000 }}
            className="neo-brutalist-table"
          />
        </div>
      </div>

      {/* Booking Detail Sidebar */}
      <div
        className={`fixed right-0 top-0 h-full bg-white border-l border-[#e5e5e5] transition-all duration-300 ease-in-out z-50 ${
          isSidebarOpen ? "w-[600px] translate-x-0" : "w-[600px] translate-x-full"
        }`}
        style={{ maxWidth: 'calc(100vw - 0px)' }}
      >
        <BookingDetailSidebar
          booking={selectedBooking}
          onClose={handleSidebarClose}
        />
      </div>
    </div>
  );
};
